/**
 * apps/web :: components/paywall/PaidSurfaceGate
 *
 * Wraps a paid surface (training, exam, rule-diff browse, /whats-next)
 * with the trial-or-entitlement decision tree (ROADS-445 FE-01).
 *
 * Decision tree:
 *   1. Trial probe in flight        → render the children's loading
 *                                      placeholder (a small i18n'd
 *                                      spinner).
 *   2. Trial probe error            → fail closed: render the paywall.
 *                                      Better to show a paywall than to
 *                                      silently grant access on a 5xx.
 *   3. Trial active                 → render children (with the
 *                                      countdown banner above when
 *                                      remaining < 6h, mounted by the
 *                                      caller's shell).
 *   4. Trial inactive               → consult the entitlement check.
 *      4a. Entitlement check loading → loading placeholder.
 *      4b. has_access true          → render children.
 *      4c. has_access false / error → render paywall.
 *
 * Why a single component: every paid route follows the same tree; the
 * tier is the only variable input.
 *
 * The gate does NOT control auth — that's handled upstream by
 * `<ProtectedRoute>`. We assume the user is authenticated by the time
 * this gate renders.
 */
import { useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import Paywall from "@/components/paywall/Paywall";
import TrialCountdownBanner from "@/components/trial/TrialCountdownBanner";
import type { EntitlementTier } from "@/lib/entitlement-api";
import { useHasAccess } from "@/hooks/useHasAccess";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import useUserProfile from "@/hooks/useUserProfile";

export interface PaidSurfaceGateProps {
  tier: EntitlementTier;
  state?: string | null;
  country?: string | null;
  children: ReactNode;
}

function LoadingPlaceholder(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="paid-surface-gate-loading"
      className="mx-auto mt-xl max-w-md p-md text-center text-neutral-700"
    >
      {t("paywall.loading", { defaultValue: "Checking your access…" })}
    </div>
  );
}

/**
 * Render the paywall inside LearnerPortalShell so the user keeps the
 * full nav while blocked. Per the FE-01 brief: "paywall component
 * renders inside the shell so nav stays accessible".
 *
 * Wave 12 Pillar B: when this gate was mounted with a tier (e.g.
 * `<PaidSurfaceGate tier="cdl">`), pass that tier through as
 * `focusTier` so the paywall renders the tier-specific card with
 * state-aware unlock CTA, not the generic 4-tier picker. Class C
 * surfaces continue to render the legacy full picker — the focused
 * variant currently lights up only when the focused tier is `cdl`.
 */
function ShelledPaywall({
  focusTier,
  focusState,
  targetStates,
}: {
  focusTier?: EntitlementTier;
  focusState?: string | null;
  targetStates?: readonly import("@/lib/states").UsStateCode[];
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <LearnerPortalShell pageTitle={t("paywall.eyebrow")}>
      <Paywall
        focusTier={focusTier}
        focusState={focusState}
        targetStates={targetStates}
      />
    </LearnerPortalShell>
  );
}

export default function PaidSurfaceGate({
  tier,
  state = null,
  country = null,
  children,
}: PaidSurfaceGateProps): JSX.Element {
  const trial = useTrialStatus();
  // ROADS-506: read profile so the Paywall branches below can hide the
  // CDL tier when the user's selected states don't support CDL content.
  // The hook is cached at module level — no extra HTTP if some upstream
  // component already triggered it.
  const profileState = useUserProfile();
  const targetStates = profileState.profile?.target_states;
  // Snapshot the local clock at the moment the trial probe first
  // resolves so the banner's countdown can re-anchor to the server's
  // `now_ms` and tick from there. Stable across re-renders.
  const probeAnchorRef = useRef<{ probeNowMs: number; localNowMs: number } | null>(null);
  if (trial.kind === "ready" && probeAnchorRef.current === null) {
    probeAnchorRef.current = {
      probeNowMs: trial.status.now_ms,
      localNowMs: Date.now(),
    };
  }
  const trialActive = trial.kind === "ready" && trial.status.active;
  // Entitlement check is short-circuited (enabled=false) when the trial
  // itself is still active — saves one round-trip. We still let the
  // hook initialise so the dependent loading state is well-defined.
  const access = useHasAccess({
    tier,
    state,
    country,
    enabled: trial.kind === "ready" && !trial.status.active,
  });

  if (trial.kind === "loading") return <LoadingPlaceholder />;

  // Trial probe failed — fail closed (paywall). Same posture for an
  // explicit "trial inactive + entitlement error".
  if (trial.kind === "error") {
    return (
      <ShelledPaywall
        focusTier={tier}
        focusState={state}
        targetStates={targetStates}
      />
    );
  }

  if (trialActive) {
    // Anchor "now" to the server's now_ms snapshot at probe-fetch time;
    // this defends the countdown against client-clock skew. The banner
    // ticks locally from there using setInterval, accepting at most a
    // few minutes of drift over a 6-hour banner window.
    const anchor = probeAnchorRef.current;
    const nowProvider = anchor
      ? () => anchor.probeNowMs + (Date.now() - anchor.localNowMs)
      : undefined;
    // Note: the countdown banner is rendered HERE rather than at the
    // shell level so a single mount of the trial fetch covers both
    // gating and banner. Banners self-suppress when remaining > 6h.
    return (
      <>
        <TrialCountdownBanner
          status={trial.status}
          ctaHref="/checkout"
          nowProvider={nowProvider}
        />
        {children}
      </>
    );
  }

  if (access.kind === "loading") return <LoadingPlaceholder />;
  if (access.kind === "error")
    return (
      <Paywall focusTier={tier} focusState={state} targetStates={targetStates} />
    );
  if (access.kind === "ready" && access.hasAccess) return <>{children}</>;
  return (
    <Paywall focusTier={tier} focusState={state} targetStates={targetStates} />
  );
}
